/**
 * QuestBlock — Renders a quest entity reference block
 *
 * Fetches quest config data and renders a card with quest name, description,
 * and link to the quests page. Display modes: Card (default), Progress, Compact.
 *
 * For authenticated users, fetches all quest configs (no single-quest endpoint exists)
 * and filters client-side. For unauthenticated users, shows a "Log in to view" CTA.
 */

import { useEffect, useState } from "react";
import { QuestsApi, QuestConfig } from "@devour/client";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useNavigate } from "react-router";

interface QuestBlockProps {
    questConfigId?: string;
    /** Rendering mode: "Card" (default), "Compact", or "Progress" */
    displayMode?: string;
    sectionTitle?: string;
}

export function QuestBlock({ questConfigId, displayMode = "Card", sectionTitle }: QuestBlockProps) {
    const navigate = useNavigate();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [quest, setQuest] = useState<QuestConfig | null>(null);
    const [loading, setLoading] = useState(true);

    const mode = displayMode?.toLowerCase() || "card";

    useEffect(() => {
        if (!questConfigId) {
            setLoading(false);
            return;
        }

        // Unauthenticated users see a CTA card, not a blank space
        if (!fullToken) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                /*
                 * No single-quest public endpoint exists — fetch all user quest configs and filter.
                 * If the target quest falls outside the first page, it won't be found here.
                 */
                const api = new QuestsApi();
                const response = await api.getQuestConfigsUserAll({});
                if (!cancelled && response?.questConfigs) {
                    const found = response.questConfigs.find(q => q.id === questConfigId);
                    if (found) setQuest(found);
                }
            } catch {
                // Silently fail — block renders fallback
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [questConfigId, fullToken]);

    if (!questConfigId) return null;

    if (loading) {
        return <div className="entity-block entity-block--loading" style={{ minHeight: 100, background: "var(--hub-card-bg, rgba(255,255,255,0.05))", borderRadius: 12, animation: "pulse 1.5s infinite" }} />;
    }

    const isCompact = mode === "compact";

    // Unauthenticated: show a CTA card prompting login
    if (!fullToken || !quest) {
        return (
            <div className={`entity-block entity-block--quest entity-block--${mode}`}>
                {sectionTitle && <h3 className="block-section-title" style={{ color: "var(--hub-text-primary, #fff)", marginBottom: "0.75rem" }}>{sectionTitle}</h3>}
                <div
                    style={{
                        background: "var(--hub-card-bg, rgba(255,255,255,0.08))",
                        borderRadius: 12,
                        padding: isCompact ? "1rem" : "1.5rem",
                        border: "1px solid var(--hub-border-subtle, rgba(255,255,255,0.1))",
                        textAlign: "center",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <span style={{ background: "#10b981", color: "#fff", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Quest</span>
                    </div>
                    <p style={{ color: "var(--hub-text-secondary, rgba(255,255,255,0.6))", fontSize: 14, margin: "0.5rem 0 0" }}>
                        {fullToken ? "Quest not available" : "Log in to view this quest"}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`entity-block entity-block--quest entity-block--${mode}`}>
            {sectionTitle && <h3 className="block-section-title" style={{ color: "var(--hub-text-primary, #fff)", marginBottom: "0.75rem" }}>{sectionTitle}</h3>}
            <div
                role="button"
                tabIndex={0}
                onClick={() => navigate("/play")}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); navigate("/play");
                    }
                }}
                style={{
                    background: "var(--hub-card-bg, rgba(255,255,255,0.08))",
                    borderRadius: 12,
                    padding: isCompact ? "1rem" : "1.5rem",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    border: "1px solid var(--hub-border-subtle, rgba(255,255,255,0.1))",
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "";
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: isCompact ? 0 : "0.5rem" }}>
                    <span style={{ background: "#10b981", color: "#fff", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Quest</span>
                    {isCompact && <span style={{ color: "var(--hub-text-primary, #fff)", fontWeight: 600, fontSize: 15 }}>{quest.name}</span>}
                </div>
                {!isCompact &&
                    <>
                        <h4 style={{ color: "var(--hub-text-primary, #fff)", fontSize: 18, fontWeight: 700, margin: "0.5rem 0 0.25rem" }}>{quest.name}</h4>
                        {quest.description &&
                            <p style={{ color: "var(--hub-text-secondary, rgba(255,255,255,0.7))", fontSize: 14, margin: 0, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{quest.description}</p>
                        }
                    </>
                }
            </div>
        </div>
    );
}
